import { AUTO_PANEL_SIZE, type PageCmsPanel, type PageCmsPanels, type PanelSize } from './panel-types';

/*
 * A partial update for one member of the config union. Plain Partial<union> collapses to the
 * common keys (just `__typename`), so the conditional type distributes Partial over each member.
 */
export type PanelConfigPatch<C = PageCmsPanel['settings']> = C extends { __typename: string }
  ? Partial<Omit<C, '__typename' | 'type' | 'size'>>
  : never;

/**
 * Sets the size of one panel in a row. Mirrors the legacy editor's `resizeRowPanels`:
 * every panel in the row is reset to "auto" first, then the chosen panel gets the new size,
 * so a 2-panel row can never contain two "wide" panels.
 *
 * Returns a new array; Apollo query results are frozen, so panels must never be mutated in place.
 */
export const updatePanelSize = (
  panels: PageCmsPanels,
  rowIndex: number,
  panelId: string,
  size: PanelSize
): PageCmsPanels =>
  panels.map((row, index) =>
    index === rowIndex
      ? row.map((panel) => ({
          ...panel,
          settings: {
            ...panel.settings,
            size: panel.id === panelId ? size : AUTO_PANEL_SIZE
          }
        }))
      : row
  );

/**
 * Removes a panel from a row; a row that becomes empty is removed entirely
 * (the grid never keeps empty rows — drop zones are rendered by the layout, not stored).
 */
export const deletePanel = (panels: PageCmsPanels, rowIndex: number, panelId: string): PageCmsPanels =>
  panels
    .map((row, index) => (index === rowIndex ? row.filter((panel) => panel.id !== panelId) : row))
    .filter((row) => row.length > 0);

/**
 * Merges a partial config patch into one panel's config. Returns new arrays/objects —
 * Apollo query results are frozen, so panels must never be mutated in place.
 */
export const updatePanelConfig = (
  panels: PageCmsPanels,
  rowIndex: number,
  panelId: string,
  patch: PanelConfigPatch
): PageCmsPanels =>
  panels.map((row, index) =>
    index === rowIndex
      ? row.map((panel) =>
          panel.id === panelId
            ? // Editors only send patches for their own settings' __typename; TS can't correlate
              // the patch member with the runtime settings member, hence the cast.
              { ...panel, settings: { ...panel.settings, ...patch } as PageCmsPanel['settings'] }
            : panel
        )
      : row
  );
